import { matchPath } from 'react-router';
import {
  CorporateViewTabs,
  getDefaultHubTab,
} from 'components/MapPage/hubNavTabs';

const INCIDENT_EXCLUSIVE_SUBPATHS = new Set([
  '',
  'dashboard',
  'calendar',
  'roster',
  'approved-forms',
  'activity-logs',
  'seat',
  'profile',
  'files',
  'settings',
]);

const INCIDENT_EXCLUSIVE_LABELS: Record<string, string> = {
  '': 'workspace',
  dashboard: 'workspace',
  calendar: 'Calendar',
  roster: 'Incident Roster',
  'approved-forms': 'Approved ICS Forms',
  'activity-logs': 'Activity Logs',
  seat: 'Position workspace',
  profile: 'Incident Profile',
  files: 'Files',
  settings: 'Settings',
};

/** Situation Unit seat slug used for the SITL COP dual-surface. */
export const SITL_SEAT_SLUG = 'situation-unit-leader';

function hubOpenTab(search: string): string | null {
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search
  );
  return params.get('openTab');
}

function isHubPath(pathname: string): boolean {
  return pathname === '/' || pathname === '/hub';
}

function incidentMatch(pathname: string) {
  return (
    matchPath({ path: '/iap/:incidentId/*', end: false }, pathname) ??
    matchPath('/iap/:incidentId', pathname)
  );
}

function incidentSubpath(pathname: string): string | null {
  const match = incidentMatch(pathname);
  if (!match) return null;
  const afterId = pathname.replace(/^\/iap\/[^/]+\/?/, '');
  return afterId.replace(/\/+$/, '').split('/')[0] ?? '';
}

function incidentIdFromPath(pathname: string): string | null {
  return incidentMatch(pathname)?.params.incidentId ?? null;
}

/** True when pathname is the Situation Unit Leader position workspace. */
export function isSitlSeatPath(pathname: string): boolean {
  return Boolean(
    matchPath(
      { path: `/iap/:incidentId/seat/${SITL_SEAT_SLUG}`, end: true },
      pathname
    )
  );
}

/**
 * SITL COP map surfaces (SitStat / Layers / Presentation) stay on the seat URL
 * and are not map-exclusive — the map can remain open without forcing /dashboard.
 */
export function isSitlCopMapSurface(pathname: string, search: string): boolean {
  if (!isSitlSeatPath(pathname)) return false;
  const tab = hubOpenTab(search);
  return (
    tab === CorporateViewTabs.SitStat ||
    tab === CorporateViewTabs.Layers ||
    tab === CorporateViewTabs.Presentation
  );
}

export function isMapExclusiveLocation(
  pathname: string,
  search: string
): boolean {
  if (pathname === '/org_files' || pathname.startsWith('/org_files/')) {
    return true;
  }
  if (pathname === '/groups' || pathname.startsWith('/groups/')) {
    return true;
  }
  if (isHubPath(pathname)) {
    return hubOpenTab(search) === CorporateViewTabs.RosterTemplates;
  }
  if (isSitlCopMapSurface(pathname, search)) {
    return false;
  }
  const subpath = incidentSubpath(pathname);
  if (subpath === null) return false;
  return INCIDENT_EXCLUSIVE_SUBPATHS.has(subpath);
}

export function getMapExclusivePageLabel(
  pathname: string,
  search: string,
  { isEscalated = false }: { isEscalated?: boolean } = {}
): string | null {
  if (!isMapExclusiveLocation(pathname, search)) return null;
  if (pathname === '/org_files' || pathname.startsWith('/org_files/')) {
    return 'Org Files';
  }
  if (pathname === '/groups' || pathname.startsWith('/groups/')) {
    return 'Org Settings';
  }
  if (isHubPath(pathname)) {
    return 'Roster Templates';
  }
  const subpath = incidentSubpath(pathname);
  if (subpath === null) return null;
  const label = INCIDENT_EXCLUSIVE_LABELS[subpath];
  if (label === 'workspace') {
    return isEscalated ? 'Planning-P' : 'ICS-201 Workspace';
  }
  return label ?? null;
}

/**
 * Where Show Map should land after the user confirms leaving an exclusive page.
 * `null` means stay on the current URL and only flip map visibility.
 */
export function getMapOpenDestination(
  pathname: string,
  { hideNotifications = false }: { hideNotifications?: boolean } = {}
): string | null {
  if (
    pathname === '/org_files' ||
    pathname.startsWith('/org_files/') ||
    pathname === '/groups' ||
    pathname.startsWith('/groups/')
  ) {
    return `/hub?openTab=${getDefaultHubTab(hideNotifications)}`;
  }
  if (isHubPath(pathname)) {
    return `/hub?openTab=${getDefaultHubTab(hideNotifications)}`;
  }
  // Situation Unit stays on the seat URL when opening the map.
  if (isSitlSeatPath(pathname)) {
    return null;
  }
  const incidentId = incidentIdFromPath(pathname);
  const subpath = incidentSubpath(pathname);
  if (!incidentId || subpath === null) return null;
  if (subpath === '' || subpath === 'dashboard') return null;
  return `/iap/${incidentId}/dashboard`;
}

/**
 * Confirming Open Map on an exclusive page navigates (or stays) and then opens
 * the map. The route-sync effect would otherwise close it again on the same
 * tick; this one-shot skip lets that confirm through.
 */
let skipNextExclusiveMapClose = false;

export function allowNextExclusiveMapOpen(): void {
  skipNextExclusiveMapClose = true;
}

export function consumeExclusiveMapOpenSkip(): boolean {
  if (!skipNextExclusiveMapClose) return false;
  skipNextExclusiveMapClose = false;
  return true;
}
